import React, { useCallback } from 'react';
import ReactSlider from 'react-slider';

import classNames from 'classnames';

import styles from './RangeSlider.module.scss';

export interface SliderProps<
    Value extends number | ReadonlyArray<number> = number
> {
    defaultValue?: this['value'] extends Value ? never : Value;
    value?: Value;
    min?: number;
    max?: number;
    minDistance?: number;
    customClass?: {
        Slider?: string;
        Thumb?: string;
        Track?: string;
    };
    pearling?: boolean;
    step?: number;
    onChange?: (value: Value, index: number) => void;
    onAfterChange?: (value: Value, index: number) => void;
    forceActive?: boolean;
}

const RangeSlider = <Value extends number | ReadonlyArray<number> = number>({
    customClass,
    min,
    max,
    value,
    defaultValue,
    minDistance,
    pearling,
    step,
    onChange,
    onAfterChange,
    forceActive,
}: SliderProps<Value>) => {
    const getActiveTrackCss = useCallback(
        (index: number) => {
            if (
                (Array.isArray(value) && value.length > 1) ||
                (Array.isArray(defaultValue) && defaultValue?.length > 1)
            ) {
                return index === 1 ? styles.Active : styles.Inactive;
            }
            return index === 1 ? styles.Inactive : styles.Active;
        },
        [defaultValue, value]
    );

    const isInitialPosition = (value: Value | undefined) => {
        if (forceActive) {
            return false;
        }
        if (Array.isArray(value)) {
            const [firstValue, lastValue] = value;
            return min === firstValue && max === lastValue;
        }
        return false;
    };

    return (
        <ReactSlider
            className={classNames(customClass?.Slider, {
                [styles.RangeSliderInitial]: isInitialPosition(
                    value ?? defaultValue
                ),
            })}
            thumbClassName={classNames(styles.RangeThumb, customClass?.Thumb)}
            renderThumb={(props) =>
                max && max > 0 ? <div {...props} /> : null
            }
            renderTrack={(props, state) => (
                <div
                    {...props}
                    className={classNames(
                        styles.RangeTrack,
                        getActiveTrackCss(state.index),
                        customClass?.Track
                    )}
                />
            )}
            min={min}
            max={max}
            defaultValue={defaultValue}
            value={value}
            pearling={pearling}
            step={step}
            minDistance={minDistance}
            onChange={onChange}
            onAfterChange={onAfterChange}
        />
    );
};

export default RangeSlider;
